import React, { useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Card,
  CardProps,
  Chip,
  Collapse,
  Container,
  Divider,
  Grid,
  Stack,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';

import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../../hooks';
import { TP_COLORS } from '../../../constants';
import { getShortName } from '../../../helper';

import { IUser, IUserExperience } from '../../../store/UserSlice';
import { getCurriculumsV2 } from '../../../store/V2/CurriculumSlice';
import { getTutor } from '../../../store/V2/TutorSlice/api';
import { getTutorsState, resetTutor } from '../../../store/V2/TutorSlice';
import { resetUserAvailabilitySlotOptionsForTrial } from '../../../store/V2/UserAvailabilitySlice';

import BasicLoader from '../../../components/Loaders/BasicLoader';
import Calendar from './components/Calendar';
import QualificationTable from './components/QualificationTable';
import ExperienceTable from './components/ExperienceTable';

const versions = [
  'Hi, I’m {name}, a passionate tutor with {years} of experience and over {hours}+ teaching hours delivered. I’m known for my patient and friendly approach, always striving to build a strong rapport with students. My interactive teaching style keeps learning engaging, while my active listening skills ensure I tailor lessons to individual needs. I’m dedicated to motivating students and helping them achieve their best.',
  'Hello, I’m {name}, an experienced tutor with over {years} of teaching expertise and more than {hours} hours of successful lessons delivered. My teaching approach is motivating and interactive, designed to create a supportive and friendly environment where students feel comfortable and confident. I’m passionate about helping students succeed and pride myself on being an active listener and guide throughout their learning journey.',
  'Hi there, I’m {name}, a tutor with {years} of experience and over {hours} teaching hours completed. I believe in creating a positive and motivating learning atmosphere that encourages students to excel. With a student-centered approach, I focus on building trust, listening actively, and adapting to their unique needs. My goal is to make learning enjoyable and effective, ensuring every student feels supported and confident in their journey to success.',
  'Hi, I’m {name}, a dedicated tutor with {years} of experience and more than {hours} teaching hours under my belt. Known for my interactive and friendly teaching style, I strive to build strong connections with students to keep them motivated and engaged. I’m passionate about helping students overcome challenges and achieve their academic goals through personalized guidance and active listening.',
];

const hashUserId = (userId) => {
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) % 1000000007;
  }
  return hash;
};

export const getRandomTutorBio = (userId, name, years, hours): string => {
  const hash = hashUserId(userId);
  const randomIndex = hash % versions.length;
  return versions[randomIndex]
    .replace('{name}', name)
    .replace('{years}', years)
    .replace('{hours}', hours);
};

export const getTotalExperienceYears = (
  experiences: IUserExperience[]
): string => {
  const totalMonths = experiences.reduce((total, exp) => {
    const start = moment(exp.startDate);
    const end = exp.endDate ? moment(exp.endDate) : moment();
    const monthsDiff = end.diff(start, 'months');
    return total + monthsDiff;
  }, 0);

  const years = totalMonths / 12 || 1;

  return `${years.toFixed()} ${years > 1 ? 'years' : 'year'}`;
};

export const getLessonDeliveredCount = (tutorData?: IUser): number => {
  const lessons = tutorData?.tutorDetails?.lessonDelivered ?? 0;
  return lessons > 250 ? Math.floor(lessons / 50) * 50 : 250;
};

const mainBoxStyles: SxProps = {
  paddingBottom: '64px',
  background: `linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 1)), url('${process.env.PUBLIC_URL}/images/tutorProfile/bg-grid.png') 0px 16px, linear-gradient(to right, #FFFFFF -2.82%, #DAF4F3 100.08%)`,
};

const backButtonProps: ButtonProps = {
  size: 'large',
  variant: 'text',
  startIcon: <ArrowBack />,
  sx: {
    top: '-22px',
    textDecoration: 'underline',
    color: TP_COLORS.gray.darken,
    borderRadius: '30px',
  },
};

const avatarStyles: SxProps = {
  marginRight: '50px',
  width: '170px',
  height: '170px',
  fontSize: '70px',
  border: '6px solid white',
};

const cardProps: CardProps = {
  variant: 'outlined',
  sx: {
    borderRadius: '20px',
    padding: '32px',
  },
};

const cardTitleProps: TypographyProps = {
  fontSize: '20px',
  fontWeight: '600',
  color: TP_COLORS.gray.darken,
  paddingBottom: '18px',
};

const chipStyles: SxProps = {
  fontSize: '12px',
  borderRadius: '4px',
  color: TP_COLORS.gray.darken,
  backgroundColor: '#CAFDF5',
  height: '27px',
};

const ratingTypoProps: TypographyProps = {
  fontSize: '37px',
  fontWeight: '800',
  lineHeight: '1',
  color: TP_COLORS.gray.darken,
};

const reviewTypoProps: TypographyProps = {
  fontSize: '14px',
  color: '#94A3B8',
  marginTop: '16px',
  maxWidth: '240px',
  textAlign: 'center',
  lineHeight: '1.2',
};

const TutorProfile: React.FC = () => {
  const urlParams = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const tutorId = urlParams.id;

  // Store
  const { tutorData } = useAppSelector(getTutorsState);

  const experienceYears = getTotalExperienceYears(
    tutorData?.profile?.experiences || []
  );
  const lessonDelivered = getLessonDeliveredCount(tutorData);
  const rating = (Number(tutorData?.tutorDetails?.rating) || 4).toFixed(1);
  const noOfReviews = tutorData?.tutorDetails?.noOfReviews || 50;

  useEffect(() => {
    if (tutorId) {
      dispatch(
        getTutor({
          id: tutorId,
        })
      );
    }

    dispatch(getCurriculumsV2());

    return () => {
      dispatch(resetTutor());
      dispatch(resetUserAvailabilitySlotOptionsForTrial());
    };
  }, []);

  return (
    <Box sx={mainBoxStyles}>
      <Container maxWidth="lg">
        <Grid container spacing={4} paddingTop="34px">
          {tutorData ? (
            <Grid item xs={12} md={7}>
              <Button
                {...backButtonProps}
                onClick={() => {
                  navigate(
                    `${process.env.PUBLIC_URL}/portal/tutors${location.search}`
                  );
                }}
              >
                Back
              </Button>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" marginBottom={2}>
                  <Avatar
                    alt="profile-pic"
                    src={tutorData.avatar}
                    sx={avatarStyles}
                  />

                  <Stack spacing={1} flex={1}>
                    <Box>
                      <Box display="flex" alignItems="center" gap="10px">
                        <Typography
                          fontSize="30px"
                          fontWeight="600"
                          lineHeight="40px"
                        >
                          {getShortName(
                            tutorData.firstname,
                            tutorData.lastname
                          )}
                        </Typography>
                        <img
                          src={`${process.env.PUBLIC_URL}/images/tutorProfile/verified-icon.png`}
                          alt="verified-icon"
                        />
                      </Box>

                      <Collapse in={Boolean(tutorData.rate)}>
                        <Typography
                          fontSize="28px"
                          fontWeight="600"
                          textAlign="right"
                        >
                          {tutorData.rate?.amount}
                          <span style={{ fontSize: '24px', marginLeft: '4px' }}>
                            {tutorData.rate?.currency}/hr
                          </span>
                        </Typography>
                      </Collapse>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/tutorProfile/medal-icon.png`}
                        alt="medal-icon"
                        width={24}
                      />
                      <Typography fontSize="18px">
                        {experienceYears} of experience
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/tutorProfile/books-icon.png`}
                        alt="books-icon"
                        width={24}
                      />
                      <Typography fontSize="18px">
                        <span style={{ color: TP_COLORS.green.light }}>
                          {lessonDelivered}+
                        </span>{' '}
                        lessons delivered
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <img
                        src={`${process.env.PUBLIC_URL}/images/tutorProfile/star-icon.png`}
                        alt="star-icon"
                        width={24}
                      />
                      <Typography fontSize="18px" color="#647488">
                        <span style={{ color: TP_COLORS.yellow.light }}>
                          {rating}
                        </span>{' '}
                        ({noOfReviews} Reviews)
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Card {...cardProps}>
                  <Typography {...cardTitleProps}>About me</Typography>
                  <Typography color="#647488" margin="10px 0">
                    {getRandomTutorBio(
                      tutorData._id,
                      getShortName(
                        tutorData.firstname,
                        tutorData.lastname,
                        false
                      ),
                      experienceYears,
                      lessonDelivered
                    )}
                  </Typography>
                  {/* <ButtonBase
                    color="secondary"
                    sx={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: TP_COLORS.gray.darken,
                      textDecoration: 'underline',
                    }}
                    onClick={() => setIsExpand((prevState) => !prevState)}
                  >
                    See {isExpand ? 'less' : 'more'}
                  </ButtonBase> */}
                </Card>

                {tutorData.profile?.languages?.length ? (
                  <Card {...cardProps}>
                    <Typography {...cardTitleProps}>I Speak</Typography>
                    <Grid container spacing="10px">
                      {tutorData.profile.languages.map((language, index) => (
                        <Grid
                          item
                          key={index}
                          display="flex"
                          alignItems="center"
                        >
                          <Typography marginRight="10px">
                            {language.language}
                          </Typography>
                          <Chip label={language.proficiency} sx={chipStyles} />
                          {tutorData.profile?.languages?.length &&
                            index < tutorData.profile.languages.length - 1 && (
                              <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ marginLeft: '10px' }}
                              />
                            )}
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                ) : null}

                {tutorData.profile?.qualifications?.length ? (
                  <Card {...cardProps}>
                    <Typography {...cardTitleProps}>
                      My Qualifications
                    </Typography>
                    <QualificationTable
                      qualifications={tutorData.profile.qualifications}
                    />
                  </Card>
                ) : null}

                {tutorData.profile?.experiences?.length ? (
                  <Card {...cardProps}>
                    <Typography {...cardTitleProps}>My Experiences</Typography>
                    <ExperienceTable
                      experiences={tutorData.profile.experiences}
                    />
                  </Card>
                ) : null}

                <Card {...cardProps}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/tutorProfile/stars-icon.png`}
                      alt="stars-icon"
                      width={92}
                    />
                    <Typography {...ratingTypoProps}>{rating}</Typography>
                    <Typography {...reviewTypoProps}>
                      This tutor is a student favorite based on {noOfReviews}{' '}
                      ratings and reviews
                    </Typography>
                  </Box>
                </Card>

                <Card {...cardProps}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/tutorProfile/verified-icon-large.png`}
                      alt="verified-icon"
                    />
                    <Typography fontSize="20px" fontWeight="700">
                      Verified Tutor
                    </Typography>
                    <Typography
                      {...reviewTypoProps}
                      marginTop="8px"
                      maxWidth="280px"
                    >
                      This tutor’s certificates and qualifications have been
                      verified by our team.
                    </Typography>
                  </Box>
                </Card>
              </Stack>
            </Grid>
          ) : (
            <Grid item xs={12} md={7}>
              <BasicLoader
                containerProps={{
                  height: '100%',
                  alignItems: 'center',
                }}
              />
            </Grid>
          )}

          <Grid item xs={12} md={5}>
            <Calendar />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );

  return null;
};

export default TutorProfile;
